import { logClientError } from "../helpers/clientLogger";

export const POLL_PRIORITY = { HIGH: 0, NORMAL: 5, LOW: 10 } as const;

type Poller = { key: string; priority: number; fetch: () => Promise<unknown> };

const INTERVAL_MS = 30_000;
// A key loaded within this window — by a scheduler poll or by usePageData's
// tab-return staleness reload (both call markFresh) — is skipped on the next
// tick, so returning to a stale tab doesn't fire the reload AND an immediate
// duplicate poll. Kept below INTERVAL_MS by enough to absorb fetch latency, so
// the steady ~30s cadence is unaffected.
const MIN_REPOLL_GAP_MS = INTERVAL_MS - 5_000;
const BASE_BACKOFF_MS = 30_000;
const MAX_BACKOFF_MS = 120_000;

const pollers = new Map<string, Poller>();
// Consumer count per key: several hooks can register the SAME poll key (e.g.
// dashboard sections sharing a period). The poller is torn down only when the
// last consumer unregisters, so one consumer leaving can't kill a poll its
// same-key siblings still depend on.
const refCounts = new Map<string, number>();
const inFlight = new Set<string>();
const backoff = new Map<string, { failures: number; nextAt: number }>();
let intervalId: ReturnType<typeof setInterval> | null = null;

export const STALE_MS = 60_000;
const lastLoadedAt = new Map<string, number>();

export function markFresh(key: string): void {
  lastLoadedAt.set(key, Date.now());
}
export function isStale(key: string, staleMs: number): boolean {
  return Date.now() - (lastLoadedAt.get(key) ?? 0) > staleMs;
}

export async function runTick(): Promise<void> {
  if (typeof document !== "undefined" && document.hidden) return;
  const ordered = [...pollers.values()].sort((a, b) => a.priority - b.priority);
  for (const poller of ordered) {
    if (inFlight.has(poller.key)) continue;
    const loadedAt = lastLoadedAt.get(poller.key);
    if (loadedAt !== undefined && Date.now() - loadedAt < MIN_REPOLL_GAP_MS) {
      continue;
    }
    const bo = backoff.get(poller.key);
    if (bo && Date.now() < bo.nextAt) continue;
    inFlight.add(poller.key);
    try {
      await poller.fetch();
      backoff.delete(poller.key);
    } catch (error) {
      logClientError(error, {
        surface: `background-poll:${poller.key}`,
        severity: "warn",
      });
      const failures = (backoff.get(poller.key)?.failures ?? 0) + 1;
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** (failures - 1), MAX_BACKOFF_MS);
      backoff.set(poller.key, { failures, nextAt: Date.now() + delay });
    } finally {
      inFlight.delete(poller.key);
    }
  }
}

export function registerPoller(poller: Poller): void {
  refCounts.set(poller.key, (refCounts.get(poller.key) ?? 0) + 1);
  pollers.set(poller.key, poller); // latest closure wins
  if (!intervalId) intervalId = setInterval(() => { void runTick(); }, INTERVAL_MS);
}

export function unregisterPoller(key: string): void {
  const next = (refCounts.get(key) ?? 0) - 1;
  if (next > 0) {
    refCounts.set(key, next);
    return;
  }
  refCounts.delete(key);
  pollers.delete(key);
  backoff.delete(key);
  if (pollers.size === 0 && intervalId) { clearInterval(intervalId); intervalId = null; }
}

export function __reset(): void { // test-only
  pollers.clear(); refCounts.clear(); inFlight.clear(); backoff.clear(); lastLoadedAt.clear();
  if (intervalId) { clearInterval(intervalId); intervalId = null; }
}
